import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AppContext } from '../../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { FaBox, FaMapMarkerAlt, FaClock, FaFileUpload, FaComment, FaPaperPlane, FaFilePdf, FaImage, FaDownload } from 'react-icons/fa';

export default function ProjectRFQ() {
  const { projectId } = useParams();
  const { API, user } = useContext(AppContext);
  const navigate = useNavigate();
  
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quotes, setQuotes] = useState({});
  
  // Chat State
  const [chatSession, setChatSession] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const chatEndRef = useRef(null);
  
  // Quote State
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [selectedSupply, setSelectedSupply] = useState(null);
  const [quoteForm, setQuoteForm] = useState({
    unitPrice: '',
    deliveryDays: '',
    notes: ''
  });

  const fetchProjectDetails = async () => {
    try {
      const response = await axios.get(`${API}/projects/public/rfq?supplier_id=${user.id}&show_all=true`);
      const foundProject = response.data.find(p => p.id === projectId);
      
      if (foundProject) {
        setProject(foundProject);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching project:', error);
      setLoading(false);
    }
  };

  const fetchExistingQuotes = async () => {
    try {
      const response = await axios.get(`${API}/projects/quotes/my-quotes/all?supplier_id=${user.id}`);
      const projectQuotes = response.data.filter(q => q.project_id === projectId);
      
      const quotesMap = {};
      projectQuotes.forEach(quote => {
        quotesMap[quote.supply_id] = quote;
      });
      setQuotes(quotesMap);
    } catch (error) {
      console.error('Error fetching quotes:', error);
    }
  };

  const initChatSession = async () => {
    try {
      const response = await axios.get(`${API}/projects/chat/session/${projectId}/${user.id}`);
      setChatSession(response.data);
    } catch (error) {
      console.error('Error initializing chat:', error);
    }
  };

  const fetchMessages = async () => {
    if (!chatSession) return;
    try {
      const response = await axios.get(`${API}/projects/chat/messages/${chatSession.id}?user_id=${user.id}`);
      setChatMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  useEffect(() => {
    if (user && projectId) {
      fetchProjectDetails();
      fetchExistingQuotes();
      initChatSession();
    }
  }, [projectId, user]);

  useEffect(() => {
    let interval;
    if (chatSession) {
      fetchMessages();
      interval = setInterval(fetchMessages, 5000);
    }
    return () => clearInterval(interval);
  }, [chatSession]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const openQuoteModal = (supply) => {
    setSelectedSupply(supply);
    setQuoteForm({ unitPrice: '', deliveryDays: '', notes: '' });
    setShowQuoteModal(true);
  };

  const handleSubmitQuote = async (e) => {
    e.preventDefault();
    
    if (!quoteForm.unitPrice || !quoteForm.deliveryDays) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      await axios.post(`${API}/projects/quotes/submit?supplier_id=${user.id}`, {
        project_id: projectId,
        supply_id: selectedSupply.id,
        unit_price: parseFloat(quoteForm.unitPrice),
        total_price: parseFloat(quoteForm.unitPrice) * selectedSupply.quantity,
        delivery_time_days: parseInt(quoteForm.deliveryDays),
        notes: quoteForm.notes || '',
        status: 'pending'
      });

      toast.success('Devis soumis avec succès');
      setShowQuoteModal(false);
      fetchExistingQuotes();
    } catch (error) {
      console.error('Error submitting quote:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la soumission');
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !chatSession) return;
    
    setSendingMessage(true);
    try {
      await axios.post(`${API}/projects/chat/send/${chatSession.id}?user_id=${user.id}`, {
        content: newMessage
      });
      setNewMessage('');
      fetchMessages();
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Format non supporté. PDF ou Images uniquement.');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 10MB)');
      return;
    }

    setUploadingFile(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const uploadRes = await axios.post(`${API}/projects/chat/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const fileData = uploadRes.data;
      
      await axios.post(`${API}/projects/chat/send/${chatSession.id}?user_id=${user.id}`, {
        content: `Document partagé: ${file.name}`,
        attachments: [fileData]
      });
      
      toast.success('Document envoyé');
      fetchMessages();
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Erreur lors du téléchargement');
    } finally {
      setUploadingFile(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0A2540] mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <p className="text-gray-600">Projet non trouvé</p>
        <button onClick={() => navigate('/supplier/opportunities')} className="ml-4 text-blue-600 hover:underline">
            Retour
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <button
            onClick={() => navigate('/supplier/opportunities')}
            className="text-[#0A2540] hover:underline mb-4"
          >
            ← Retour aux opportunités
          </button>
          
          <h1 className="text-3xl font-bold text-[#0A2540] mb-2">
            {project.project_name}
          </h1>
          
          <div className="flex gap-4 text-gray-600">
            <span className="flex items-center gap-2">
              <FaMapMarkerAlt />
              {project.location}
            </span>
            {project.delivery_deadline && (
              <span className="flex items-center gap-2">
                <FaClock />
                Échéance: {project.delivery_deadline}
              </span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <h2 className="text-xl font-bold text-[#0A2540] mb-4">
              Fournitures Demandées ({project.supplies?.length || 0})
            </h2>

            {project.supplies?.map((supply) => {
              const existingQuote = quotes[supply.id];
              
              return (
                <Card key={supply.id}>
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-lg font-bold text-[#0A2540] mb-1">
                          <FaBox className="inline mr-2" />
                          {supply.name}
                        </h3>
                        <p className="text-gray-600 text-sm">
                          Quantité: <strong>{supply.quantity} {supply.unit}</strong>
                        </p>
                        {supply.specifications && (
                          <p className="text-gray-600 text-sm mt-1">
                            Spécifications: {supply.specifications}
                          </p>
                        )}
                        <p className="text-sm mt-2">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            supply.type === 'rental' 
                              ? 'bg-blue-100 text-blue-700' 
                              : 'bg-[#635BFF]/10 text-[#5248d9]'
                          }`}>
                            {supply.type === 'rental' ? '🔑 Location' : '🛒 Achat'}
                          </span>
                        </p>
                      </div>

                      {existingQuote ? (
                        <div className="text-right">
                          <span className="px-3 py-1 bg-[#635BFF]/10 text-[#5248d9] rounded-full text-sm font-semibold">
                            ✓ Devis soumis
                          </span>
                          <p className="text-sm text-gray-600 mt-2">
                            {existingQuote.total_price}€
                          </p>
                        </div>
                      ) : (
                        <button
                          onClick={() => openQuoteModal(supply)}
                          className="bg-[#635BFF] text-white px-4 py-2 rounded-lg hover:bg-[#5248d9] text-sm whitespace-nowrap"
                        >
                          Soumettre un devis
                        </button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="space-y-4">
            <Card className="h-[600px] flex flex-col">
              <div className="p-4 border-b bg-[#0A2540] text-white rounded-t-lg">
                <h3 className="text-lg font-bold flex items-center gap-2">
                  <FaComment />
                  Discussion avec le client
                </h3>
                <p className="text-xs text-blue-100">
                  Posez vos questions ou envoyez des documents
                </p>
              </div>
              
              <CardContent className="flex-1 p-0 flex flex-col overflow-hidden">
                <div className="flex-1 overflow-y-auto p-4 bg-gray-50 space-y-4">
                  {chatMessages.length === 0 ? (
                    <div className="text-center text-gray-400 mt-10">
                      <FaComment className="text-4xl mx-auto mb-2 opacity-20" />
                      <p>Commencez la discussion...</p>
                    </div>
                  ) : (
                    chatMessages.map((msg) => {
                      const isMe = msg.sender_id === user.id;
                      return (
                        <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[80%] rounded-lg p-3 ${
                            isMe ? 'bg-[#0A2540] text-white' : 'bg-white border border-gray-200 shadow-sm'
                          }`}>
                            {!isMe && <p className="text-xs font-bold text-[#0A2540] mb-1">{msg.sender_name}</p>}
                            
                            {msg.content && <p className="text-sm whitespace-pre-wrap">{msg.content}</p>}
                            
                            {msg.attachments && msg.attachments.length > 0 && (
                              <div className="mt-2 space-y-1">
                                {msg.attachments.map((file, idx) => (
                                  <a 
                                    key={idx}
                                    href={`${API}${file.url}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`block text-xs p-2 rounded flex items-center gap-2 ${
                                      isMe ? 'bg-[#003a52] hover:bg-[#002d40]' : 'bg-gray-100 hover:bg-gray-200'
                                    }`}
                                  >
                                    {file.type === 'pdf' ? <FaFilePdf className="text-red-400" /> : <FaImage className="text-blue-400" />}
                                    <span className="truncate flex-1">{file.filename}</span>
                                    <FaDownload />
                                  </a>
                                ))}
                              </div>
                            )}
                            
                            <p className={`text-[10px] mt-1 text-right ${isMe ? 'text-blue-200' : 'text-gray-400'}`}>
                              {new Date(msg.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                              {msg.is_read && isMe && <span className="ml-1">✓✓</span>}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={chatEndRef} />
                </div>

                <div className="p-3 border-t bg-white">
                  <div className="flex gap-2 items-end">
                    <label className="cursor-pointer text-gray-500 hover:text-[#0A2540] p-2">
                      <input
                        type="file"
                        accept=".pdf,image/*"
                        onChange={handleFileUpload}
                        disabled={uploadingFile}
                        className="hidden"
                      />
                      <FaFileUpload className={uploadingFile ? "animate-pulse" : ""} size={20} />
                    </label>
                    
                    <textarea
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                      placeholder="Votre message..."
                      className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm max-h-24 resize-none focus:outline-none focus:ring-1 focus:ring-[#0A2540]"
                      rows="1"
                    />
                    
                    <button
                      onClick={handleSendMessage}
                      disabled={!newMessage.trim() || sendingMessage}
                      className="bg-[#635BFF] text-white p-2 rounded-full hover:bg-[#5248d9] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FaPaperPlane size={16} />
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {showQuoteModal && selectedSupply && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-[#0A2540] mb-4">
                Soumettre un devis
              </h3>
              
              <div className="mb-4 p-3 bg-gray-50 rounded">
                <p className="font-semibold text-[#0A2540]">{selectedSupply.name}</p>
                <p className="text-sm text-gray-600">
                  Quantité: {selectedSupply.quantity} {selectedSupply.unit}
                </p>
                {selectedSupply.specifications && (
                  <p className="text-sm text-gray-600">
                    Spécifications: {selectedSupply.specifications}
                  </p>
                )}
              </div>

              <form onSubmit={handleSubmitQuote}>
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Prix unitaire (€) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={quoteForm.unitPrice}
                    onChange={(e) => setQuoteForm({...quoteForm, unitPrice: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    placeholder="ex: 150.00"
                    required
                  />
                  {quoteForm.unitPrice && (
                    <p className="text-xs text-gray-500 mt-1">
                      Prix total: {(parseFloat(quoteForm.unitPrice) * selectedSupply.quantity).toFixed(2)}€
                    </p>
                  )}
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Délai de livraison (jours) *
                  </label>
                  <input
                    type="number"
                    value={quoteForm.deliveryDays}
                    onChange={(e) => setQuoteForm({...quoteForm, deliveryDays: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    placeholder="ex: 7"
                    required
                  />
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Notes ou conditions (optionnel)
                  </label>
                  <textarea
                    value={quoteForm.notes}
                    onChange={(e) => setQuoteForm({...quoteForm, notes: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 h-24"
                    placeholder="Conditions de paiement, garanties, etc."
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowQuoteModal(false)}
                    className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-[#635BFF] text-white px-4 py-2 rounded-lg hover:bg-[#5248d9]"
                  >
                    Soumettre
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
